import '../styles/LessonCard.css';
import '../styles/LessonStatus.css';

import React, { MouseEvent, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import AlertDialog from '../../shared/components/AlertDialog';
import AuthService from '../../shared/services/AuthService';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import CertIcon from './CertIcon';
import CertService from '../../report/services/CertService';
import Chip from '@mui/material/Chip';
import ExpandLessIcon from '@mui/icons-material/ExpandLessTwoTone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileService from '../../shared/services/FileService';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import LessonService from '../services/LessonService';
import LockIcon from './LockIcon';
import Overlay from '../../shared/components/Overlay';
import Paper from '@mui/material/Paper';
import PathwayLessonVm from '../models/LessonVm';
import Typography from '@mui/material/Typography';
import WizardSessionCard from './WizardSessionCard';

interface LessonCardProps {
  lesson: PathwayLessonVm;
  filteredByStatus: boolean;
  onLaunchPathwayLesson: (
    lessonWindow: Window,
    title: string,
    resultkey: string
  ) => void;
}

const PathwayLessonCard = (props: LessonCardProps) => {
  const { title, cert, wizard } = props.lesson;
  const status = LessonService.toStatus(props.lesson);
  const [showSessions, setShowSessions] = useState(false);
  const [fetchingCert, setFetchingCert] = useState(false);
  const [fetchCertError, setFetchCertError] = useState(false);

  const numSessions = props.lesson.wizard ? props.lesson.results.length : 0;
  const ExpandIcon = showSessions
    ? () => <ExpandLessIcon />
    : () => <ExpandMoreIcon />;

  function handleCardClick(e: MouseEvent<HTMLDivElement>) {
    handleCardClickV1();
  }

  function handleCardClickV1() {
    if (status === 'L') {
      return;
    }

    if (props.lesson.wizard) {
      setShowSessions(!showSessions);
    } else {
      const lessonWindow = window.open();
      props.onLaunchPathwayLesson(
        lessonWindow,
        props.lesson.title,
        props.lesson.results[0].resultkey
      );
    }
  }

  const createWizardSession = () => {
    const lessonWindow = window.open();
    props.onLaunchPathwayLesson(lessonWindow, props.lesson.title, '');
  };

  const handleCertIconClick = (lessonkey: string) => {
    setFetchingCert(true);
    CertService.getCertFile(AuthService.Username, lessonkey)
      .then((data) => {
        setFetchingCert(false);
        FileService.open(data);
      })
      .catch(() => {
        setFetchingCert(false);
        setFetchCertError(true);
      });
  };

  const handleCertErrorClose = () => {
    setFetchCertError(false);
  };

  return (
    <Box m="8px 0" style={{ cursor: status === 'L' ? 'default' : 'pointer' }}>
      <Paper
        className={numSessions > 0 ? 'expandable-card' : ''}
        style={{ padding: '8px' }}
        onClick={handleCardClick}
      >
        <Grid container alignItems="center" wrap="nowrap" spacing={1}>
          {props.lesson.wizard && (
            <Grid>
              <Chip
                size="small"
                avatar={
                  <Avatar>
                    <ExpandIcon />
                  </Avatar>
                }
                label={numSessions}
                style={{ cursor: 'pointer' }}
              />
            </Grid>
          )}
          <Grid
            style={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            <Typography variant="body1" noWrap={true}>
              {title}
            </Typography>
          </Grid>
          <Grid>
            <Box marginRight={2}>
              {cert && (
                <CertIcon
                  status={LessonService.toCertStatus(status, cert)}
                  onClick={(e: MouseEvent<HTMLDivElement>) => {
                    e.stopPropagation();
                    handleCertIconClick(`${props.lesson.lessonkey}`);
                  }}
                />
              )}
            </Box>
          </Grid>
          <Grid style={{ width: '100px', textAlign: 'center' }}>
            <Grid container spacing={1}>
              {status === 'L' && (
                <Grid>
                  <LockIcon />
                </Grid>
              )}
              <Grid>
                <Typography
                  variant="button"
                  className={`lesson-status ${status}`}
                  style={{ color: LessonService.getStatusColor(status) }}
                >
                  {LessonService.toStatusText(status)}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      {
        // * Wizard sessions
        props.lesson.wizard && (showSessions || props.filteredByStatus) && (
          <Grid container>
            <Grid>
              <IconButton color="primary" onClick={createWizardSession}>
                <AddIcon />
              </IconButton>
            </Grid>
            <Grid style={{ flex: 1 }}>
              <div style={{ marginBottom: '4px' }}>
                {props.lesson.results.map((result) => (
                  <WizardSessionCard
                    key={result.resultkey}
                    pathkey={props.lesson.pathkey}
                    lessonResult={result}
                    onLaunchLesson={props.onLaunchPathwayLesson}
                  />
                ))}
              </div>
            </Grid>
          </Grid>
        )
      }
      {fetchingCert && <Overlay spinner message="Downloading certificate..." />}
      {fetchCertError && (
        <AlertDialog
          title="View Certificate"
          message="Failed to download the certificate."
          onClose={handleCertErrorClose}
        />
      )}
    </Box>
  );
};

export default PathwayLessonCard;
